import type { ClubConfig, Customer } from 'shopify-types'

/**
 * Shopify customer tag that marks a customer as eligible for the B&S Club during
 * the release phase. Follows the existing `Club:*` tag convention (e.g.
 * `Club:Bronze`). Assigned to the chosen subset of customers in the Shopify
 * admin while {@link ClubConfig.releasePhaseActive} is `true`.
 */
export const CLUB_ELIGIBLE_TAG = 'Club:Eligible'

/**
 * Shopify customer tag that marks a customer as an actual club member, i.e. one
 * who has manually joined via the join flow (see `/api/club/join`). Membership
 * activation happens out of band, so the tag may appear asynchronously rather
 * than on the very next request.
 *
 * Eligibility ({@link CLUB_ELIGIBLE_TAG}) only governs whether a customer may
 * *see* the club; membership governs whether they see the club dashboard or the
 * join UI.
 */
export const CLUB_MEMBER_TAG = 'Club:Member'

/** CMS `accountPages` slug of the club page, gated by club eligibility. */
export const CLUB_PAGE_SLUG = 'club'

/** Route of the club page (without locale prefix), as produced by `pageToUrl`. */
export const CLUB_PAGE_URL = `/account/${CLUB_PAGE_SLUG}`

/**
 * Route of the set creator ("Wunschset") page (without locale prefix), as
 * produced by `pageToUrl`. Currently open to everyone regardless of login or
 * club membership.
 */
export const SET_CREATOR_PAGE_URL = '/shop/wunschset'

/**
 * Whether a customer may access the club.
 *
 * While the release phase is active, only customers carrying
 * {@link CLUB_ELIGIBLE_TAG} are eligible. Switching `releasePhaseActive` to
 * `false` in the `ClubConfig` metaobject opens the club to everyone — the
 * single, code-free lever for the full rollout.
 *
 * This is the *access* gate (can the customer open the club page at all). Club
 * *membership* is a separate, finer gate handled by {@link isClubMember}.
 */
export const isClubEligible = (
  customer: Pick<Customer, 'tags'> | null | undefined,
  clubConfig: ClubConfig | null | undefined,
): boolean => {
  if (!clubConfig?.releasePhaseActive) return true

  return (customer?.tags ?? []).includes(CLUB_ELIGIBLE_TAG)
}

/**
 * Whether a customer has manually joined the club.
 *
 * Membership is purely tag-based ({@link CLUB_MEMBER_TAG}): customers without
 * the tag are shown the join UI instead of the club dashboard. A missing or
 * not-yet-hydrated customer counts as a non-member.
 */
export const isClubMember = (customer: Pick<Customer, 'tags'> | null | undefined): boolean =>
  (customer?.tags ?? []).includes(CLUB_MEMBER_TAG)
